import type ExcelJS from 'exceljs';
import {
    buildHeaderIndex,
    ExcelImportError,
    type RawRow,
} from './excelImportMeta';
import type { TemplateColumnKey } from '../purchaseHeader/purchaseTemplateMeta';

/**
 * exceljs adapter: loads an uploaded `.xlsx` and lifts the Products sheet into
 * normalized {@link RawRow}s. The only file that touches exceljs on the read
 * path — dynamically imported (same as the template builder) so the ~1 MB
 * dependency stays out of the main bundle.
 *
 * The Products sheet is taken **by position** (first worksheet) — its display
 * name is localized, so never select by name.
 */

/** Hard cap on data rows so a runaway sheet can't freeze the tab. */
export const MAX_DATA_ROWS = 1000;

const pad = (value: number): string => String(value).padStart(2, '0');

/**
 * Excel date cells come back as JS `Date`s anchored near UTC midnight; local
 * formatting can shift a day in negative-offset timezones. Shifting by +12h
 * and reading UTC components rounds to the intended calendar day.
 */
const dateToIso = (value: Date): string => {
    const shifted = new Date(value.getTime() + 12 * 60 * 60 * 1000);
    return `${shifted.getUTCFullYear()}-${pad(shifted.getUTCMonth() + 1)}-${pad(
        shifted.getUTCDate(),
    )}`;
};

/** Normalize any exceljs cell value to a trimmed string ('' when empty). */
export const cellToString = (value: ExcelJS.CellValue): string => {
    if (value === null || value === undefined) return '';
    if (value instanceof Date) return dateToIso(value);
    if (typeof value === 'number') return String(value);
    if (typeof value === 'boolean') return value ? 'true' : 'false';
    if (typeof value === 'string') return value.trim();
    if (typeof value === 'object') {
        if ('richText' in value) {
            return value.richText
                .map((part) => part.text)
                .join('')
                .trim();
        }
        if ('error' in value) return '';
        if ('result' in value) {
            return cellToString(value.result as ExcelJS.CellValue);
        }
        if ('text' in value) {
            return cellToString(value.text as ExcelJS.CellValue);
        }
    }
    return String(value).trim();
};

/**
 * Load the workbook and lift the first worksheet into RawRows. Throws
 * {@link ExcelImportError} (`readFailed` / `notTemplate` / `emptyFile` /
 * `tooManyRows`) for every failure mode the modal shows copy for.
 */
export const parseProductsSheet = async (
    buffer: ArrayBuffer,
    labelToKey: Map<string, TemplateColumnKey>,
): Promise<RawRow[]> => {
    const { Workbook } = (await import('exceljs')).default;
    const workbook = new Workbook();
    try {
        await workbook.xlsx.load(buffer);
    } catch {
        throw new ExcelImportError('readFailed');
    }

    const sheet = workbook.worksheets[0];
    if (!sheet) {
        throw new ExcelImportError('notTemplate');
    }

    const headerRow = sheet.getRow(1);
    const headerCells: string[] = [];
    for (let column = 1; column <= sheet.columnCount; column += 1) {
        headerCells.push(cellToString(headerRow.getCell(column).value));
    }
    const headerIndex = buildHeaderIndex(headerCells, labelToKey);
    if (!headerIndex) {
        throw new ExcelImportError('notTemplate');
    }

    const rows: RawRow[] = [];
    for (let rowNumber = 2; rowNumber <= sheet.rowCount; rowNumber += 1) {
        const row = sheet.getRow(rowNumber);
        const cells: RawRow['cells'] = {};
        let hasContent = false;
        for (const [cellIndex, key] of headerIndex) {
            const value = cellToString(row.getCell(cellIndex + 1).value);
            cells[key] = value;
            if (value !== '') hasContent = true;
        }
        if (!hasContent) continue; // skip fully blank rows
        rows.push({ rowNumber, cells });
        if (rows.length > MAX_DATA_ROWS) {
            throw new ExcelImportError('tooManyRows');
        }
    }

    if (rows.length === 0) {
        throw new ExcelImportError('emptyFile');
    }
    return rows;
};
